import { Dataset, JSONObject } from '../../types/types';
import { buildLocateRequestData } from './buildLocateRequestData';
import { fetchRowIds } from './fetchRowIds';

type ProbeRowIndexesByIdsParams = {
  dataset: Dataset;
  activeFilters: Record<string, unknown> | null;
  activeSorting: any[];
  sessionToken: string | null;
  lockedCachedQueryId: string;
  itemIds: string[];
  // Subset probes are scoped to their master row.
  masterRowId?: string;
  // Dataset control data — must scope the probe to the same set as the grid.
  datasetControl?: JSONObject;
};

export type ProbeRowIndexesByIdsResult = {
  // Ascending index of every wanted row that exists in the result.
  indexes: number[];
  farthestIndex: number;
  farthestId: string;
  totalRowCount: number;
  resolvedFilters: Record<string, unknown>;
  resolvedSorting: any[];
};

// One id-only probe → position of every wanted row (rows themselves are NOT
// fetched — the probe payload is just the id column, cheap even for large
// datasets). Null on transport failure, cache mismatch, or when no wanted
// row exists.
export const probeRowIndexesByIds = async ({
  dataset,
  activeFilters,
  activeSorting,
  sessionToken,
  lockedCachedQueryId,
  itemIds,
  masterRowId,
  datasetControl,
}: ProbeRowIndexesByIdsParams): Promise<ProbeRowIndexesByIdsResult | null> => {
  const { idItem, resolvedFilters, resolvedSorting, requestData } =
    buildLocateRequestData(
      dataset,
      activeFilters,
      activeSorting,
      sessionToken,
      masterRowId,
      lockedCachedQueryId || null,
      datasetControl
    );

  const probe = await fetchRowIds({
    dataset,
    requestData,
    resolvedFilters,
    resolvedSorting,
    idItem,
    sessionToken,
  });
  if (!probe) return null;
  if (
    lockedCachedQueryId &&
    probe.cachedQueryId &&
    probe.cachedQueryId !== lockedCachedQueryId
  ) {
    return null;
  }

  const wantedIds = new Set(itemIds.map(String));
  const indexes: number[] = [];
  let farthestId: string | null = null;
  probe.ids.forEach((row, index) => {
    const id = String(row?.[idItem]);
    if (!wantedIds.has(id)) return;
    indexes.push(index);
    farthestId = id;
  });
  if (indexes.length === 0 || farthestId === null) return null;

  return {
    indexes,
    farthestIndex: indexes[indexes.length - 1],
    farthestId,
    totalRowCount: probe.ids.length,
    resolvedFilters,
    resolvedSorting,
  };
};
